import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ApiGo from '../../actions/ApiGo';
import { ADD_BEDS_ARRAY } from '../../stores/reducer_beds';
import { CTXHide } from '../Layout';

// 匯入格式: MAC,card_type,institution,reserved(TR編號),bed_name（以逗號分隔，五段皆必填）
// ex: "2CCF67BBA222,LightPad,9A,1,01"，需與後端 /txtimport 的驗證規則一致
const MAC_FIELDS = [
    { key: 'mac', label: 'PAD.FIELD_MAC' },
    { key: 'card_type', label: 'PAD.FIELD_CARD_TYPE' },
    { key: 'institution', label: 'PAD.FIELD_INSTITUTION' },
    { key: 'reserved', label: 'PAD.FIELD_RESERVED' },
    { key: 'bed_name', label: 'PAD.FIELD_BED_NAME' },
];
const MAC_REGEX = /^[0-9A-Fa-f]{12}$/;

// 移除註解行（去掉前方空白後以 # 開頭的整行），其餘內容交給 JSON.parse
// 只處理行首的 #，JSON 字串內容裡的 # 不受影響
function stripCommentLines(text) {
    return text
        .split(/\r?\n/)
        .filter(line => !line.trimStart().startsWith('#'))
        .join('\n');
}

/**
 * 驗證單筆 macs 字串
 * @returns {{ hasErr: boolean, fileErr?: boolean, segmentErr?: number, errors: Object }}
 *  - fileErr: 該筆不是字串（JSON 結構錯誤）
 *  - segmentErr: 段數不是 5 時，記錄實際段數
 *  - errors: 各欄位的錯誤 { key: { msg, params } }
 */
function validateMacs(macs, existingMacs, macCount) {
    if (typeof macs !== 'string') {
        return { hasErr: true, fileErr: true, errors: {} };
    }

    const segments = macs.split(',');
    if (segments.length !== MAC_FIELDS.length) {
        return { hasErr: true, segmentErr: segments.length, errors: {} };
    }

    const errors = {};
    MAC_FIELDS.forEach(({ key }, i) => {
        if (segments[i].trim() === '') { errors[key] = { msg: 'PAD.MAC_ERROR_EMPTY' }; }
    });

    const mac = segments[0];
    if (!errors.mac) {
        if (mac.length !== 12) {
            errors.mac = { msg: 'PAD.MAC_ERROR_LENGTH', params: { length: mac.length } };
        } else if (!MAC_REGEX.test(mac)) {
            errors.mac = { msg: 'PAD.MAC_ERROR_FORMAT' };
        } else if (existingMacs.has(mac.toUpperCase())) {
            errors.mac = { msg: 'PAD.MAC_ERROR_DUPLICATE' };
        } else if (macCount[mac.toUpperCase()] > 1) {
            errors.mac = { msg: 'PAD.MAC_ERROR_DUPLICATE_IN_FILE' };
        }
    }

    return { hasErr: Object.keys(errors).length > 0, errors };
}

const AddPads = () => {
    const ctx_hide = useContext(CTXHide);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // 取得現有智慧墊清單
    const store_pads_array = useSelector(state => state.store_beds.beds_array ?? []);

    const [file, setFile] = useState({});
    const [fileName, setFileName] = useState('');
    const [UpdateRedux, setUpdateRedux] = useState(false);
    const [response, setResponse] = useState(null);

    // 所有列一次驗證完，contentErr 由全部列的結果決定（避免最後一列覆蓋前面的錯誤）
    const validations = useMemo(() => {
        const existingMacs = new Set(
            store_pads_array.map(bed => bed.mac).filter(Boolean).map(mac => String(mac).toUpperCase())
        );
        const macCount = {};
        Object.values(file).forEach(pads => {
            const mac = typeof pads?.macs === 'string' ? pads.macs.split(',')[0].toUpperCase() : null;
            if (mac) { macCount[mac] = (macCount[mac] ?? 0) + 1; }
        });
        return Object.values(file).map(pads => validateMacs(pads?.macs, existingMacs, macCount));
    }, [file, store_pads_array]);
    const contentErr = validations.some(v => v.hasErr);

    useEffect(() => {
        if (UpdateRedux) {
            ApiGo.getBed()
                .then(res => {
                    dispatch(ADD_BEDS_ARRAY(res.beds));
                    setUpdateRedux(false);
                })
                .catch(err => {
                    console.log(err);
                    setUpdateRedux(false);
                });
        }
    }, [UpdateRedux, dispatch]);

    function handleFileInput(e) {
        const _file = e.target.files[0];
        // 清空 input，讓修改後重新選同一個檔案也能觸發 onChange
        e.target.value = '';
        if (!_file) return;

        const _reader = new FileReader();

        _reader.readAsText(_file);
        _reader.onloadend = () => {
            setFileName(_file.name);
            let _content;
            try {
                _content = JSON.parse(stripCommentLines(_reader.result));
            } catch {
                _content = null;
            }
            // 必須是物件或陣列，否則視為檔案格式錯誤
            if (_content === null || typeof _content !== 'object' || Object.keys(_content).length === 0) {
                _content = { error: {} };
            }
            setFile(_content);
            setResponse(null);
        };
    }

    // 將 API 錯誤轉成顯示文字
    function errorMessage(err) {
        const detail = typeof err?.response?.detail === 'string' ? `（${err.response.detail}）` : '';
        switch (err?.status) {
            case 400: return t('PAD.ERROR_400') + detail;
            case 401: return t('PAD.ERROR_401');
            case 409: return t('PAD.PAD_EXIST_409');
            case 422: return t('PAD.ERROR_422');
            case undefined: return t('PAD.ERROR_NETWORK');
            default: return t('PAD.ERROR_UNKNOWN', { status: err.status }) + detail;
        }
    }

    function handleSubmission() {
        // 沒有上傳任何檔案
        if (!file || Object.keys(file).length === 0) {
            setResponse({ status: 'NO_FILE' });
            return;
        }

        if (contentErr) {
            setResponse({ status: 'CONTENT_ERR' });
            return;
        }

        const _entries = Object.entries(file);
        Promise.allSettled(_entries.map(([, pads]) => ApiGo.postTxtimport(pads)))
            .then(results => {
                const failed = [];
                results.forEach((r, index) => {
                    if (r.status === 'rejected') {
                        console.log(r.reason);
                        failed.push({ index, msg: errorMessage(r.reason) });
                    }
                });
                const successCount = results.length - failed.length;

                if (successCount > 0) { setUpdateRedux(true); }

                if (failed.length === 0) {
                    setResponse({ status: 201 });
                    setFile({});
                    setFileName('');
                } else {
                    // 只保留失敗的列，避免重送時已成功的列變成重複
                    const failedSet = new Set(failed.map(f => f.index));
                    setFile(Object.fromEntries(_entries.filter((_, index) => failedSet.has(index))));
                    setResponse({ status: 'PARTIAL', successCount, failed });
                }
            });
    }

    function responseContent() {
        switch (response?.status) {
            case 'NO_FILE': return t('PAD.NO_FILE_UPLOADED');
            case 'CONTENT_ERR': return t('PAD.CONTENT_ERROR_FIX_FIRST');
            case 'PARTIAL':
                return (
                    <div className="w-100">
                        <div>{t('PAD.UPLOAD_RESULT', { success: response.successCount, fail: response.failed.length })}</div>
                        {response.failed.map(f => (
                            <div key={f.index}>{t('PAD.PAD_INDEX_PREFIX')}{f.index}：{f.msg}</div>
                        ))}
                    </div>
                );
            default: return t('PAD.SUCCESS_201');
        }
    }

    // 全部成功時關閉新增頁；其餘情況只關閉彈窗，讓使用者可以修正後重送
    const closeResponse = response?.status === 201 ? ctx_hide : () => setResponse(null);

    const response_div = (
        <div
            className="pos-f z-1 wid-full hig-full top-0 left-0 fx-ccc bg-black"
            onClick={closeResponse}
        >
            <div
                className="w-40 bgC_white br-10 fx-ccc font-ml ovy-a pad-10"
                style={{ minHeight: '10%', maxHeight: '70%' }}
                onClick={e => e.stopPropagation()}
            >
                {responseContent()}
            </div>
        </div>
    );

    return (
        <>
            {response !== null ? response_div : null}

            <div className="h-10"></div>

            <div className="w-100 h-80 font-ml">
                <div className="w-100 h-15 fx-d-r fx-fsc">
                    <div className="btn_type _title w-15 h-50 fx-ccc">{t('PAD.UPLOAD')}</div>
                    <div className="h-50 w-20 fx-d-c fx-fec">
                        {fileName}
                        <div className="h-0 w-100 _line"></div>
                    </div>
                    <label className="w-15 h-50 type_file fx-ccc">
                        {t('PAD.FIND_FILE')}
                        <input type="file" className="dis_none" onChange={handleFileInput} />
                    </label>
                </div>

                <div className="w-100 h-15 fx-d-r fx-fsc">
                    <div className="btn_type _title w-15 h-50 fx-ccc">{t('PAD.RESPONED')}</div>
                </div>

                <div className="w-100 h-60 mac_input beige_border pad-31 font-l ovy-a">
                    {
                        validations.map((result, index) => (
                            <MacRowBar key={index} index={index} result={result} />
                        ))
                    }
                </div>
            </div>

            <div className="wrap-personal-btn w-100 h-10 fx-fec">
                <div className="h-100 l-s-container fx-d-r">
                    <div
                        className={`fx-ccc w-50 ${contentErr ? 'btn_type_add macError' : 'btn_type'}`}
                        onClick={handleSubmission}
                    >
                        {t('GLOBAL.CONFIRM')}
                    </div>
                    <div className="fx-ccc w-50 btn_type" onClick={ctx_hide}>
                        {t('GLOBAL.CANCEL')}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddPads;

// 顯示單筆驗證結果（驗證邏輯在 validateMacs）
function MacRowBar({ index, result }) {
    const { t } = useTranslation();

    let content;
    if (result.fileErr) {
        content = <div>{t('PAD.FILE_FORMAT_ERROR')}</div>;
    } else if (result.segmentErr !== undefined) {
        content = <div>{t('PAD.MAC_ERROR_SEGMENT', { count: result.segmentErr })}</div>;
    } else if (result.hasErr) {
        content = (
            <div className="w-90">
                {MAC_FIELDS.filter(({ key }) => result.errors[key]).map(({ key, label }) => (
                    <div key={key} className="fx-d-r w-100">
                        <div className="w-15">{t(label)}</div>
                        <div className="w-85">{t(result.errors[key].msg, result.errors[key].params)}</div>
                    </div>
                ))}
            </div>
        );
    } else {
        content = <div>{t('PAD.MAC_NO_ERROR')}</div>;
    }

    return (
        <div className="fx-d-r w-100 mar-10">
            <div className="w-10">{t('PAD.PAD_INDEX_PREFIX')}{index}</div>
            {content}
        </div>
    );
}
